/**
 * Perpustakaan Media.
 *
 * Menyimpan foto yang dipakai konten website. Berkas disajikan publik pada
 * https://keuangan.abduljalal.org/media/<uuid> sehingga situs abduljalal.org
 * dapat memakainya langsung.
 */
import { useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Input,
  Modal,
  PageHeader,
  SkeletonKartu,
  EmptyState,
  ErrorState,
} from '@/components'
import { api, unggah, pesanKesalahan } from '@/lib/api'
import { formatTanggal } from '@/lib/format'
import { useAksi, usePaged } from '@/lib/useApi'
import { useToast } from '@/components/Toast'

interface Media {
  id: string
  publicUrl: string
  fileName: string
  originalName: string | null
  mimeType: string
  sizeBytes: number
  alt: string | null
  caption: string | null
  createdAt: string
}

function ukuranRapi(byte: number): string {
  if (byte < 1024) return `${byte} B`
  if (byte < 1024 * 1024) return `${(byte / 1024).toFixed(0)} KB`
  return `${(byte / (1024 * 1024)).toFixed(1)} MB`
}

export default function Media() {
  const toast = useToast()
  const aksi = useAksi()
  const [cari, setCari] = useState('')
  const [sedangUnggah, setSedangUnggah] = useState(false)
  const [galatUnggah, setGalatUnggah] = useState<string | null>(null)
  const [pratinjau, setPratinjau] = useState<Media | null>(null)
  const [alt, setAlt] = useState('')

  const daftar = usePaged<Media>('/media', { perHalaman: 24, query: { q: cari.trim() || undefined } })

  const pilihBerkas = async (daftarBerkas: FileList | null) => {
    if (!daftarBerkas || daftarBerkas.length === 0) return
    setSedangUnggah(true)
    setGalatUnggah(null)
    try {
      for (const berkas of Array.from(daftarBerkas)) {
        const fd = new FormData()
        fd.append('file', berkas)
        if (alt.trim()) fd.append('alt', alt.trim())
        await unggah<Media>('/media', fd)
      }
      setAlt('')
      daftar.muat()
      toast.sukses(`${daftarBerkas.length} gambar terunggah.`)
    } catch (e) {
      setGalatUnggah(pesanKesalahan(e))
    } finally {
      setSedangUnggah(false)
    }
  }

  const hapus = (m: Media) =>
    aksi.jalankan(() => api.delete(`/media/${m.id}`), {
      sukses: 'Gambar dihapus.',
      setelah: () => {
        setPratinjau(null)
        daftar.muat()
      },
    })

  const salinTautan = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.sukses('Tautan gambar disalin.')
    } catch {
      toast.gagal('Browser menolak akses papan klip. Salin manual dari kolom tautan.')
    }
  }

  return (
    <div>
      <PageHeader
        judul="Media Website"
        deskripsi="Gambar untuk sampul berita dan galeri. Format JPG, PNG, WEBP, GIF, AVIF (maks 10 MB per berkas)."
      />

      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            label="Cari"
            placeholder="Nama berkas atau keterangan"
            value={cari}
            onChange={(e) => setCari(e.target.value)}
          />
          <Input
            label="Teks alternatif (opsional)"
            placeholder="Deskripsi singkat untuk aksesibilitas & SEO"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-netral-700">Unggah gambar</label>
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
              className="block w-full text-sm text-netral-600"
              disabled={sedangUnggah}
              onChange={(e) => void pilihBerkas(e.target.files)}
            />
            {sedangUnggah ? <p className="mt-1 text-xs text-netral-500">Mengunggah…</p> : null}
            {galatUnggah ? <p className="mt-1 text-xs text-red-600">{galatUnggah}</p> : null}
          </div>
        </div>
      </Card>

      {daftar.memuat && daftar.items.length === 0 ? (
        <SkeletonKartu jumlah={6} />
      ) : daftar.galat ? (
        <ErrorState pesan={daftar.galat} kode={daftar.kodeGalat} onCoba={daftar.muat} />
      ) : daftar.items.length === 0 ? (
        <EmptyState
          judul="Belum ada gambar"
          deskripsi="Unggah foto kegiatan untuk dipakai sebagai sampul berita dan galeri."
        />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {daftar.items.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setPratinjau(m)}
              className="group overflow-hidden rounded-lg border border-netral-200 bg-white text-left transition hover:border-yayasan-400 hover:shadow-sm"
            >
              <img src={m.publicUrl} alt={m.alt ?? m.originalName ?? ''} className="h-32 w-full object-cover" loading="lazy" />
              <div className="space-y-1 p-2">
                <p className="truncate text-xs font-medium text-netral-800">{m.originalName ?? m.fileName}</p>
                <p className="text-[11px] text-netral-500">
                  {ukuranRapi(m.sizeBytes)} · {formatTanggal(m.createdAt)}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {daftar.meta && daftar.meta.totalPages > 1 ? (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-netral-600">
            Halaman {daftar.meta.page} dari {daftar.meta.totalPages} · {daftar.meta.total} gambar
          </p>
          <div className="flex gap-2">
            <Button
              ukuran="kecil"
              varian="garis"
              disabled={(daftar.meta.page ?? 1) <= 1}
              onClick={() => daftar.setHalaman((daftar.meta?.page ?? 1) - 1)}
            >
              Sebelumnya
            </Button>
            <Button
              ukuran="kecil"
              varian="garis"
              disabled={(daftar.meta.page ?? 1) >= daftar.meta.totalPages}
              onClick={() => daftar.setHalaman((daftar.meta?.page ?? 1) + 1)}
            >
              Berikutnya
            </Button>
          </div>
        </div>
      ) : null}

      <Modal
        terbuka={Boolean(pratinjau)}
        onTutup={() => setPratinjau(null)}
        judul={pratinjau?.originalName ?? 'Gambar'}
        lebar="max-w-2xl"
        footer={
          pratinjau ? (
            <div className="flex justify-end gap-2">
              <Button varian="garis" onClick={() => void salinTautan(pratinjau.publicUrl)}>
                Salin tautan
              </Button>
              <Button varian="bahaya" memuat={aksi.memuat} onClick={() => hapus(pratinjau)}>
                Hapus
              </Button>
            </div>
          ) : null
        }
      >
        {pratinjau ? (
          <div className="space-y-3">
            <img src={pratinjau.publicUrl} alt={pratinjau.alt ?? ''} className="max-h-80 w-full rounded object-contain bg-netral-50" />
            <div className="space-y-1 text-sm text-netral-700">
              <p>
                <span className="text-netral-500">Tautan publik:</span>{' '}
                <span className="break-all font-mono text-xs">{pratinjau.publicUrl}</span>
              </p>
              <p>
                <span className="text-netral-500">Ukuran:</span> {ukuranRapi(pratinjau.sizeBytes)} ·{' '}
                {pratinjau.mimeType}
              </p>
              <p>
                <span className="text-netral-500">Diunggah:</span> {formatTanggal(pratinjau.createdAt)}
              </p>
              {pratinjau.alt ? <p className="text-netral-500">Alt: {pratinjau.alt}</p> : null}
            </div>
            <p className="text-xs text-netral-500">
              Gambar yang sedang dipakai sebagai sampul konten tidak dapat dihapus — lepas sampulnya lebih dulu.
            </p>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
